import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Clock, User, Share2, Linkedin, Twitter, Facebook, Bot, Zap, BarChart3, MessageSquare } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useBlogContactPopup } from "@/hooks/useBlogContactPopup";
import DemoScheduler from "@/components/DemoScheduler";
import SEO from "@/components/SEO";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";

const BlogPostGestaoChamados = () => {
    const { isOpen, setIsOpen } = useBlogContactPopup(3000);

    return (
        <div className="min-h-screen bg-background">
            <SEO
                title="IA na Gestão de Chamados 2025: Guia Completo"
                description="Descubra como a Inteligência Artificial Generativa está transformando o Service Desk em 2025. Assistentes virtuais inteligentes, suporte preditivo e redução de 60% nos custos operacionais."
                canonical="https://aroan.com.br/blog/ia-gestao-chamados-2025"
                ogImage="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&auto=format&fit=crop"
                author="Maria Silva"
                publishedTime="2024-11-08T00:00:00-03:00"
                keywords="IA em service desk, gestão de chamados, assistente virtual, suporte preditivo, automação de TI, LLM, chatbot inteligente, VIA"
            />
            <BreadcrumbSchema
                items={[
                    { name: "Home", url: "https://aroan.com.br" },
                    { name: "Blog", url: "https://aroan.com.br/blog" },
                    { name: "IA na Gestão de Chamados 2025: Guia Completo" }
                ]}
            />
            <Header />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 bg-gradient-hero overflow-hidden">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge className="mb-6 bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            IA e Automação
                        </Badge>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-8 leading-tight animate-fade-in">
                            IA na <span className="text-primary">Gestão de Chamados 2025</span>: Guia Completo
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-delay-1">
                            <div className="flex items-center gap-2">
                                <User className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">Maria Silva</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">08 Nov, 2024</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                <span className="font-medium text-foreground">8 min de leitura</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Featured Image */}
            <section className="pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden shadow-2xl animate-scale-in">
                        <img
                            src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&auto=format&fit=crop"
                            alt="IA na Gestão de Chamados"
                            className="w-full h-[400px] sm:h-[500px] object-cover"
                        />
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-background">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mx-auto">

                        {/* Introduction */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <p className="lead text-xl sm:text-2xl text-muted-foreground leading-relaxed font-medium">
                                O modelo tradicional de Service Desk está quebrado. Filas intermináveis, agentes sobrecarregados e usuários frustrados são a norma em muitas empresas. Mas 2025 marca o ponto de virada.
                            </p>
                            <p>
                                A Inteligência Artificial Generativa não é mais apenas uma promessa futurista; ela está reescrevendo as regras do suporte de TI e atendimento ao cliente. Não estamos falando apenas de chatbots simples, mas de sistemas cognitivos capazes de resolver problemas complexos autonomamente.
                            </p>
                        </div>

                        <hr className="my-12 border-border" />

                        {/* Main Content */}
                        <div className="space-y-12 mb-12">

                            {/* Section 1 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <MessageSquare className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">O Fim do "Ping-Pong" de Chamados</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Todos conhecem a dança: o usuário abre um chamado, o agente pede mais detalhes, o usuário demora a responder, o agente escala o ticket... e dias se passam. Com LLMs (Large Language Models) integrados, a IA analisa o contexto inicial, pede as informações faltantes instantaneamente e, em muitos casos, sugere a solução correta em segundos, não dias.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Section 2 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <Zap className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">Suporte Preditivo: A Era da Invisibilidade</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            O melhor suporte é aquele que você nem sabe que aconteceu. Algoritmos de IA agora monitoram logs de sistemas e padrões de comportamento para identificar falhas antes que elas afetem o usuário final. Imagine um sistema que reinicia um serviço travado ou libera espaço em disco automaticamente, notificando a TI apenas para revisão.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Section 3 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <Bot className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">Assistentes Virtuais que Realmente Ajudam</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Esqueça os menus de opções limitadas. Os assistentes de 2025, como o VIA da AROAN, entendem linguagem natural, contexto e intenção. Eles podem executar ações complexas como "resetar a senha do usuário X e conceder acesso à pasta Y", validando permissões e executando scripts de automação em tempo real.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Section 4 */}
                            <div className="bg-card rounded-2xl p-8 border border-border hover:shadow-lg transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="bg-primary/10 p-3 rounded-lg">
                                        <BarChart3 className="w-8 h-8 text-primary" />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold mb-3">Redução de Custos Operacionais</h3>
                                        <p className="text-muted-foreground leading-relaxed">
                                            Empresas que adotaram IA na gestão de chamados relatam uma redução média de 60% nos custos operacionais. Isso não significa demitir sua equipe, mas sim liberá-la de tarefas repetitivas (reset de senha, dúvidas frequentes) para focar em projetos estratégicos e inovação.
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Conclusion */}
                        <div className="prose prose-lg dark:prose-invert max-w-none mb-12">
                            <h2 className="text-3xl font-bold mb-6 text-foreground">A Vantagem Competitiva</h2>
                            <p>
                                A adoção de IA na gestão de serviços não é mais um diferencial "nice-to-have", é uma questão de sobrevivência e competitividade. Empresas que continuarem dependendo de processos manuais e reativos ficarão para trás em eficiência e satisfação do cliente.
                            </p>
                            <p>
                                O futuro é autônomo, proativo e instantâneo. Sua empresa está pronta?
                            </p>
                        </div>

                        {/* Share */}
                        <div className="flex items-center justify-between border-y border-border py-6 mb-12">
                            <span className="font-semibold">Compartilhe este artigo:</span>
                            <div className="flex gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-600"
                                    onClick={() => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Linkedin className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-400"
                                    onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent("Como IA Está Revolucionando a Gestão de Chamados em 2025")}`, '_blank')}
                                >
                                    <Twitter className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="hover:text-blue-700"
                                    onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`, '_blank')}
                                >
                                    <Facebook className="w-5 h-5" />
                                </Button>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={async () => {
                                        if (navigator.share) {
                                            try {
                                                await navigator.share({
                                                    title: 'Como IA Está Revolucionando a Gestão de Chamados em 2025',
                                                    text: 'Descubra como assistentes virtuais inteligentes estão transformando o atendimento ao cliente.',
                                                    url: window.location.href,
                                                });
                                            } catch (err) {
                                                console.error('Error sharing:', err);
                                            }
                                        } else {
                                            navigator.clipboard.writeText(window.location.href);
                                            alert("Link copiado para a área de transferência!");
                                        }
                                    }}
                                >
                                    <Share2 className="w-5 h-5" />
                                </Button>
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="bg-foreground text-background rounded-2xl p-8 sm:p-12 text-center">
                            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
                                Quer ver o futuro na prática?
                            </h2>
                            <p className="text-background/80 mb-8 max-w-2xl mx-auto">
                                Agende uma demonstração do VIA e veja como nossa IA pode transformar seu Service Desk em dias, não meses.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button size="lg" variant="secondary" className="group" onClick={() => window.open('/#demo', '_self')}>
                                    Conhecer o VIA
                                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                                </Button>
                                <Button size="lg" variant="outline" className="bg-transparent border-background/20 text-background hover:bg-background/10" onClick={() => window.open('/#roi', '_self')}>
                                    Calcular Economia
                                </Button>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            <Footer />

            <DemoScheduler open={isOpen} onOpenChange={setIsOpen} />
        </div>
    );
};

export default BlogPostGestaoChamados;
